/**
 * React hook that maintains a persistent SSE connection for notifications (feat-69).
 *
 * Parses incoming SSE events and dispatches to ShellEventBus:
 *   task.created / task.progress / task.completed / task.failed  → AGENT_STATUS_CHANGED
 *   hil.created                                  → AGENT_APPROVAL_REQUESTED
 *   system.broadcast                             → TOAST_NOTIFICATION
 *
 * Reconnect lives in the shared fetch-SSE client. This hook consumes the
 * shell-owned multiplexed hub in notifications.ts.
 * Mounts once in Layout — stays active when ESA panel is collapsed.
 */

import { useEffect } from 'react';
import type { ShellEvent } from '@growthaxl/web-foundation';
import { shellEventBus } from '../../../shell/events/eventBus';
import { subscribeNotifications } from '../../../common/services/notifications';
import type { SseEvent } from '../../../common/services/fetchSse';
import { useEsaDomainStore } from '../store/esaDomainStore';
import { HOST_NOTIFICATION_EVENT_TYPES } from '../../../constants/eventNames';

interface NotificationEnvelope {
  id?: string;
  event_type?: string;
  payload?: {
    id?: string;
    type?: string;
    status?: string;
    title?: string;
    summary?: string | null;
    conversation_id?: string;
    stages_completed?: number;
    stages_total?: number;
    stage_label?: string;
  };
  target?: { type?: string; value?: string | null };
}

function parseEnvelope(data: string): NotificationEnvelope | null {
  try {
    return JSON.parse(data) as NotificationEnvelope;
  } catch {
    console.warn('[useNotificationStream] failed to parse event data', data);
    return null;
  }
}

function dispatchEnvelope(eventType: string, envelope: NotificationEnvelope): void {
  const payload = envelope.payload ?? {};

  // Mark inbox as having unread content — cleared when the user opens inbox.
  useEsaDomainStore.getState().setHasUnreadNotification(true);

  // Route to per-tab unread flag: hil.* → Agent Requests, everything else → Notifications.
  if (eventType.startsWith('hil.')) {
    useEsaDomainStore.getState().setUnreadRequests(true);
  } else {
    useEsaDomainStore.getState().setUnreadNotifications(true);
  }

  if (
    eventType === HOST_NOTIFICATION_EVENT_TYPES.TASK_CREATED ||
    eventType === HOST_NOTIFICATION_EVENT_TYPES.TASK_PROGRESS ||
    eventType === HOST_NOTIFICATION_EVENT_TYPES.TASK_COMPLETED ||
    eventType === HOST_NOTIFICATION_EVENT_TYPES.TASK_FAILED
  ) {
    shellEventBus.dispatch({
      type: 'AGENT_STATUS_CHANGED',
      payload: {
        agentId: payload.id ?? '',
        status: payload.status ?? eventType,
        message: payload.title,
        summary: payload.summary ?? undefined,
        conversationId: payload.conversation_id,
        responseType: payload.type,
        notificationId: envelope.id,
        stagesCompleted: payload.stages_completed,
        stagesTotal: payload.stages_total,
        stageLabel: payload.stage_label,
      },
    } as ShellEvent);
    return;
  }

  if (eventType === HOST_NOTIFICATION_EVENT_TYPES.HIL_CREATED) {
    const isHilRequest = payload.type === 'hil_request';
    const hilId = isHilRequest ? payload.id : undefined;
    const taskId = !isHilRequest ? payload.id : undefined;

    shellEventBus.dispatch({
      type: 'AGENT_APPROVAL_REQUESTED',
      payload: {
        agentId: payload.id ?? '',
        requestId: hilId ?? payload.id ?? envelope.id ?? '',
        hilId,
        conversationId: payload.conversation_id,
        taskId,
        title: payload.title,
        summary: payload.summary ?? undefined,
      },
    } as ShellEvent);
    return;
  }

  if (eventType === HOST_NOTIFICATION_EVENT_TYPES.SYSTEM_BROADCAST) {
    shellEventBus.dispatch({
      type: 'TOAST_NOTIFICATION',
      payload: {
        message: payload.title ?? 'Platform notification',
        severity: 'info',
      },
    });
    return;
  }

  console.debug('[useNotificationStream] unhandled event_type', eventType);
}

function handleSseEvent(sseEvent: SseEvent): void {
  const envelope = parseEnvelope(sseEvent.data);
  if (!envelope) return;
  const eventType =
    sseEvent.event && sseEvent.event !== 'message'
      ? sseEvent.event
      : envelope.event_type ?? '';
  if (eventType) {
    dispatchEnvelope(eventType, envelope);
  }
}

/**
 * React hook that maintains a persistent SSE connection for notifications.
 * Mounts once in Layout — stays active when ESA panel is collapsed.
 *
 * @param isAuthenticated - Only open the SSE stream when auth is complete.
 *   Prevents 401s from race conditions where Layout mounts before the token
 *   is available in sessionStorage.
 */
export function useNotificationStream(isAuthenticated: boolean): void {
  useEffect(() => {
    if (!isAuthenticated) return;

    return subscribeNotifications(handleSseEvent);
  }, [isAuthenticated]);
}
